import { resolveMediaUrl } from './imageUrl';

// In-memory cache of detected natural video dimensions keyed by clean URL
const videoDimensionsCache = new Map();
const preconnectedOrigins = new Set();

let activeMetadataWarmup = null;

/**
 * Extract a clean URL string from either a string or an object wrapper.
 * Rejects literal "null" or "undefined" strings.
 * @param {string|object|null|undefined} value
 * @returns {string}
 */
function extractUrlString(value) {
    if (!value) return '';
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed === 'null' || trimmed === 'undefined' ? '' : trimmed;
    }
    if (typeof value === 'object') {
        const candidate =
            value.optimized_url ||
            value.optimizedUrl ||
            value.stream_url ||
            value.streamUrl ||
            value.url ||
            value.secure_url ||
            value.path ||
            value.src ||
            value.video_url ||
            value.videoUrl ||
            '';
        if (typeof candidate !== 'string') return '';
        const trimmed = candidate.trim();
        return trimmed === 'null' || trimmed === 'undefined' ? '' : trimmed;
    }
    return '';
}

/**
 * Strip hash fragments from a media URL for stable cache keys.
 * @param {string} url
 * @returns {string}
 */
function normalizeCacheKeyUrl(url) {
    if (!url || typeof url !== 'string') return '';
    return url.split('#')[0].trim();
}

/**
 * Append a deterministic version query parameter for `/uploads/...` files ONLY when
 * an explicit update timestamp exists on the record. Preserves HTTP 206 Partial Content
 * byte-range browser caching across replays and Next/Previous navigation.
 *
 * @param {string|null} resolvedUrl
 * @param {string|number|Date|null} updatedAt
 * @returns {string|null}
 */
function appendVideoCacheVersion(resolvedUrl, updatedAt) {
    if (!resolvedUrl || !updatedAt) return resolvedUrl;
    if (!resolvedUrl.includes('/uploads/') || resolvedUrl.includes('?')) {
        return resolvedUrl;
    }

    const ts = new Date(updatedAt).getTime();
    if (Number.isFinite(ts) && ts > 0) {
        return `${resolvedUrl}?v=${ts}`;
    }
    return resolvedUrl;
}

/**
 * Optimize unsigned Cloudinary `/video/upload/` URLs for smooth web streaming:
 * - `f_auto`: delivers MP4/WebM with `moov` atom at the start (`faststart`)
 * - `vc_auto`: selects hardware-accelerated codec (H.264 / VP9)
 * - `q_auto:good`: prevents raw 40-80 Mbps mobile camera bitrates from stalling playback while preserving crisp visual detail
 * - `w_1280,c_limit` (portrait/square) or `w_1920,c_limit` (landscape): caps oversized 4K uploads without upscaling smaller clips
 *
 * Leaves non-Cloudinary, local `/uploads/`, signed (`/s--`), and pre-transformed URLs untouched.
 *
 * @param {string|null} url
 * @param {string} orientation
 * @returns {string|null}
 */
function optimizeShortVideoDeliveryUrl(url, orientation = 'unknown') {
    if (!url || typeof url !== 'string') return url;
    if (!url.includes('res.cloudinary.com/') || !url.includes('/video/upload/')) {
        return url;
    }
    if (url.includes('/s--')) {
        return url;
    }

    const marker = '/video/upload/';
    const idx = url.indexOf(marker);
    if (idx === -1) return url;

    const afterUpload = url.slice(idx + marker.length);
    if (!afterUpload) return url;

    const firstSegment = afterUpload.split('/')[0] || '';
    const hasExistingTransform =
        firstSegment.includes(',') ||
        /^(?:f|q|vc|ac|br|w|h|c|sp|so|eo|du|dpr|fl|e|l|o|r|bo)_/i.test(firstSegment);

    if (hasExistingTransform) {
        return url;
    }

    const maxWidth =
        orientation === 'portrait' || orientation === 'square' ? 1280 : 1920;
    return `${url.slice(
        0,
        idx + marker.length
    )}f_auto,q_auto:good,vc_auto,w_${maxWidth},c_limit/${afterUpload}`;
}

/**
 * Optimize unsigned Cloudinary `/image/upload/` thumbnail URLs for fast card & poster decode.
 *
 * @param {string|null} url
 * @returns {string|null}
 */
function optimizeShortPosterUrl(url) {
    if (!url || typeof url !== 'string') return url;
    if (!url.includes('res.cloudinary.com/') || !url.includes('/image/upload/')) {
        return url;
    }
    if (url.includes('/s--')) {
        return url;
    }

    const marker = '/image/upload/';
    const idx = url.indexOf(marker);
    if (idx === -1) return url;

    const afterUpload = url.slice(idx + marker.length);
    if (!afterUpload) return url;

    const firstSegment = afterUpload.split('/')[0] || '';
    const hasExistingTransform =
        firstSegment.includes(',') ||
        /^(?:f|q|w|h|c|dpr|fl|e|g|ar)_/i.test(firstSegment);

    if (hasExistingTransform) {
        return url;
    }

    return `${url.slice(0, idx + marker.length)}f_auto,q_auto,w_720,c_limit/${afterUpload}`;
}

/**
 * Check if browser Data Saver or ultra-slow 2G connection is active.
 * @returns {boolean}
 */
export function isConstrainedNetwork() {
    if (typeof navigator === 'undefined') return false;
    const conn =
        navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection;
    if (!conn) return false;
    if (conn.saveData) return true;
    const eff = String(conn.effectiveType || '').toLowerCase();
    return eff === 'slow-2g' || eff === '2g';
}

/**
 * Establish early DNS/TCP/TLS connection to a video or embed host once per session.
 * @param {string|null|undefined} mediaUrl
 */
export function preconnectMediaOrigin(mediaUrl) {
    if (!mediaUrl || typeof document === 'undefined' || typeof window === 'undefined') {
        return;
    }

    try {
        const parsed = new URL(mediaUrl, window.location.origin);
        if (
            parsed.origin === window.location.origin ||
            preconnectedOrigins.has(parsed.origin)
        ) {
            return;
        }
        preconnectedOrigins.add(parsed.origin);

        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = parsed.origin;
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
    } catch (_) {
        // Ignore malformed URLs
    }
}

/**
 * Read cached natural video dimensions if previously detected.
 * @param {string|null|undefined} url
 * @returns {object|null}
 */
export function getCachedVideoDimensions(url) {
    const key = normalizeCacheKeyUrl(url);
    if (!key) return null;
    return videoDimensionsCache.get(key) || null;
}

/**
 * Cache natural video dimensions by URL so repeat views / modal opens avoid re-probing.
 * @param {string|null|undefined} url
 * @param {number} width
 * @param {number} height
 * @returns {object|null}
 */
export function setCachedVideoDimensions(url, width, height) {
    const key = normalizeCacheKeyUrl(url);
    const classified = classifyAspectRatio(width, height);
    if (!key || !classified) return null;

    const entry = {
        ...classified,
        width: Number(width),
        height: Number(height),
        source: 'video'
    };
    videoDimensionsCache.set(key, entry);
    return entry;
}

/**
 * Cancel any background metadata warm-up immediately so an active video player
 * gets 100% of available HTTP connection and decoder resources.
 */
export function cancelVideoMetadataWarmup() {
    if (activeMetadataWarmup && typeof activeMetadataWarmup.abort === 'function') {
        activeMetadataWarmup.abort();
        activeMetadataWarmup = null;
    }
}

/**
 * Lightly warm HTTP byte-range headers & natural dimensions for a single video URL
 * (e.g., on card hover or for the next video in modal) without downloading full video data.
 * Strictly single-concurrency and automatically skipped on Data Saver / 2G connections.
 *
 * @param {string|null|undefined} videoSrc
 * @returns {Promise<object|null>}
 */
export function warmShortVideoMetadata(videoSrc) {
    const key = normalizeCacheKeyUrl(videoSrc);
    if (!key || typeof document === 'undefined') {
        return Promise.resolve(null);
    }

    const cached = videoDimensionsCache.get(key);
    if (cached) {
        return Promise.resolve(cached);
    }

    if (isConstrainedNetwork()) {
        return Promise.resolve(null);
    }

    if (activeMetadataWarmup && activeMetadataWarmup.key === key) {
        return activeMetadataWarmup.promise;
    }

    cancelVideoMetadataWarmup();
    preconnectMediaOrigin(key);

    let abortFn = null;
    const promise = new Promise((resolve) => {
        const video = document.createElement('video');
        let settled = false;

        const finish = (result) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            video.onloadedmetadata = null;
            video.onerror = null;
            try {
                video.pause();
                video.removeAttribute('src');
                video.load();
            } catch (_) {
                // Ignore detached media cleanup errors
            }
            if (activeMetadataWarmup && activeMetadataWarmup.key === key) {
                activeMetadataWarmup = null;
            }
            resolve(result);
        };

        abortFn = () => finish(null);
        const timer = setTimeout(() => finish(null), 4000);

        video.preload = 'metadata';
        video.muted = true;
        video.playsInline = true;

        video.onloadedmetadata = () => {
            const entry = setCachedVideoDimensions(
                key,
                video.videoWidth,
                video.videoHeight
            );
            finish(entry);
        };

        video.onerror = () => finish(null);
        video.src = key;
    });

    activeMetadataWarmup = {
        key,
        promise,
        abort: abortFn
    };

    return promise;
}

/**
 * Extract YouTube 11-character video ID from varied URL formats or direct IDs.
 * Supports:
 * - https://youtube.com/shorts/m9BrA-eB86k
 * - https://www.youtube.com/shorts/m9BrA-eB86k?feature=share
 * - https://m.youtube.com/shorts/m9BrA-eB86k
 * - https://www.youtube.com/watch?v=m9BrA-eB86k
 * - https://youtu.be/m9BrA-eB86k
 * - https://www.youtube.com/embed/m9BrA-eB86k
 * - https://www.youtube-nocookie.com/embed/m9BrA-eB86k
 * - Direct 11-char ID (e.g. m9BrA-eB86k) when not a file path
 *
 * @param {string} urlOrId
 * @returns {string|null} 11-char video ID or null
 */
export function extractYouTubeId(urlOrId) {
    if (!urlOrId || typeof urlOrId !== 'string') return null;
    const trimmed = urlOrId.trim();
    if (!trimmed) return null;

    // Relative paths or video filenames are never YouTube IDs
    if (
        trimmed.startsWith('/') ||
        trimmed.startsWith('./') ||
        trimmed.startsWith('../') ||
        trimmed.includes('/uploads/') ||
        trimmed.includes('\\') ||
        /\.(mp4|webm|mov|m4v|ogg|ogv|mkv|avi)(?:[?#]|$)/i.test(trimmed)
    ) {
        return null;
    }

    // Check if it's already an 11-character YouTube video ID
    if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) {
        return trimmed;
    }

    // Match shorts, watch, live, embed, youtube-nocookie, or youtu.be patterns
    const regex =
        /(?:(?:www\.|m\.)?youtube\.com\/(?:shorts\/|watch\?(?:.*&)?v=|embed\/|live\/|v\/)|(?:www\.)?youtube-nocookie\.com\/embed\/|youtu\.be\/)([a-zA-Z0-9_-]{11})/i;
    const match = trimmed.match(regex);
    return match ? match[1] : null;
}

/**
 * Detect whether a URL points to a direct or backend-hosted video file (MP4, WebM, MOV, M4V, OGG)
 * @param {string} url
 * @returns {boolean}
 */
export function isDirectVideoUrl(url) {
    if (!url || typeof url !== 'string') return false;
    const trimmed = url.trim();
    if (!trimmed) return false;

    if (trimmed.startsWith('blob:') || trimmed.startsWith('data:video/')) {
        return true;
    }

    const normalized = trimmed.replace(/\\/g, '/');
    const cleanPath = normalized.split(/[?#]/)[0].toLowerCase();

    if (
        cleanPath.endsWith('.mp4') ||
        cleanPath.endsWith('.webm') ||
        cleanPath.endsWith('.mov') ||
        cleanPath.endsWith('.m4v') ||
        cleanPath.endsWith('.ogg') ||
        cleanPath.endsWith('.ogv')
    ) {
        return true;
    }

    // Relative backend upload paths or cloud video delivery paths
    if (
        cleanPath.startsWith('/uploads/') ||
        cleanPath.startsWith('uploads/') ||
        cleanPath.includes('/uploads/video-shorts/') ||
        cleanPath.includes('/video-shorts/') ||
        cleanPath.includes('/video/upload/')
    ) {
        return true;
    }

    return false;
}

/**
 * Detect known third-party iframe embed player URLs (non-YouTube)
 * @param {string} url
 * @returns {boolean}
 */
function isKnownExternalEmbedUrl(url) {
    if (!url || typeof url !== 'string') return false;
    const lower = url.toLowerCase();
    return (
        lower.includes('player.vimeo.com/') ||
        lower.includes('tiktok.com/embed') ||
        lower.includes('facebook.com/plugins/video') ||
        lower.includes('dailymotion.com/embed') ||
        (lower.includes('instagram.com/') && lower.includes('/embed'))
    );
}

/**
 * Classify numeric width/height (or aspect ratio) into a clean responsive orientation
 * and CSS aspect-ratio modifier class without stretching or cropping.
 *
 * @param {number} width
 * @param {number} height
 * @returns {{ ratio: number, orientation: 'landscape'|'portrait'|'square', aspectClass: string } | null}
 */
export function classifyAspectRatio(width, height) {
    const w = Number(width);
    const h = Number(height);
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
        return null;
    }

    const ratio = w / h;
    if (!Number.isFinite(ratio) || ratio <= 0) {
        return null;
    }

    if (ratio >= 1.5) {
        return { ratio, orientation: 'landscape', aspectClass: 'aspect-16-9' };
    }
    if (ratio > 1.1) {
        return { ratio, orientation: 'landscape', aspectClass: 'aspect-4-3' };
    }
    if (ratio >= 0.9) {
        return { ratio, orientation: 'square', aspectClass: 'aspect-1-1' };
    }
    if (ratio >= 0.68) {
        return { ratio, orientation: 'portrait', aspectClass: 'aspect-4-5' };
    }
    return { ratio, orientation: 'portrait', aspectClass: 'aspect-9-16' };
}

/**
 * Infer initial orientation & aspect class from optional backend metadata or URL structure
 * before HTML5 <video> metadata finishes loading in the browser.
 */
function inferInitialOrientation(item, rawUrl, youtubeId, rawSourceType) {
    // 1. Explicit numeric dimensions from backend if available
    const rawW = item.width || item.video_width || item.videoWidth;
    const rawH = item.height || item.video_height || item.videoHeight;
    const fromDims = classifyAspectRatio(rawW, rawH);
    if (fromDims) {
        return { ...fromDims, isKnown: true };
    }

    // 2. Explicit aspect_ratio string/number from backend (e.g. "16:9", "9:16", 1.77)
    const rawAspect = item.aspect_ratio || item.aspectRatio;
    if (typeof rawAspect === 'number' && rawAspect > 0) {
        const fromNum = classifyAspectRatio(rawAspect, 1);
        if (fromNum) return { ...fromNum, isKnown: true };
    } else if (typeof rawAspect === 'string' && rawAspect.trim()) {
        const parts = rawAspect.trim().split(/[:/xX]/);
        if (parts.length === 2) {
            const fromPair = classifyAspectRatio(parts[0], parts[1]);
            if (fromPair) return { ...fromPair, isKnown: true };
        }
        const parsedFloat = Number(rawAspect.trim());
        if (Number.isFinite(parsedFloat) && parsedFloat > 0) {
            const fromFloat = classifyAspectRatio(parsedFloat, 1);
            if (fromFloat) return { ...fromFloat, isKnown: true };
        }
    }

    // 3. Explicit orientation string from backend
    const explicitOrientation = String(
        item.orientation || item.video_orientation || item.videoOrientation || ''
    )
        .trim()
        .toLowerCase();
    if (explicitOrientation === 'landscape' || explicitOrientation === 'horizontal') {
        return { ratio: 16 / 9, orientation: 'landscape', aspectClass: 'aspect-16-9', isKnown: true };
    }
    if (explicitOrientation === 'portrait' || explicitOrientation === 'vertical') {
        return { ratio: 9 / 16, orientation: 'portrait', aspectClass: 'aspect-9-16', isKnown: true };
    }
    if (explicitOrientation === 'square') {
        return { ratio: 1, orientation: 'square', aspectClass: 'aspect-1-1', isKnown: true };
    }

    // 4. YouTube URL structure: /shorts/ or bare 11-char ID vs standard watch/youtu.be/live URLs
    if (youtubeId) {
        const trimmedUrl = String(rawUrl || '').trim();
        const lowerUrl = trimmedUrl.toLowerCase();
        const rawBadge = String(item.badge || item.tag || '').trim().toLowerCase();
        const isBareYoutubeId = /^[a-zA-Z0-9_-]{11}$/.test(trimmedUrl);
        const isShortUrl =
            lowerUrl.includes('/shorts/') ||
            isBareYoutubeId ||
            rawSourceType.includes('short') ||
            rawBadge === 'shorts' ||
            rawBadge === 'short';

        if (isShortUrl) {
            return {
                ratio: 9 / 16,
                orientation: 'portrait',
                aspectClass: 'aspect-9-16',
                isKnown: true,
                isYouTubeShort: true
            };
        }

        return {
            ratio: 16 / 9,
            orientation: 'landscape',
            aspectClass: 'aspect-16-9',
            isKnown: true,
            isYouTubeShort: false
        };
    }

    // 5. Third-party embeds (TikTok/Instagram reels default portrait; Vimeo/Dailymotion default landscape)
    if (rawUrl) {
        const lower = rawUrl.toLowerCase();
        if (lower.includes('tiktok.com') || lower.includes('/reel/')) {
            return { ratio: 9 / 16, orientation: 'portrait', aspectClass: 'aspect-9-16', isKnown: true };
        }
        if (lower.includes('vimeo.com') || lower.includes('dailymotion.com')) {
            return { ratio: 16 / 9, orientation: 'landscape', aspectClass: 'aspect-16-9', isKnown: true };
        }
    }

    // 6. Uploaded / direct videos without explicit DB dimensions will be measured dynamically
    // via HTML5 <video onLoadedMetadata> on the storefront.
    return {
        ratio: null,
        orientation: 'unknown',
        aspectClass: null,
        isKnown: false
    };
}

/**
 * Normalize a raw video short record from the backend into a standardized frontend object.
 * Supports both directly uploaded video files (`source_type === 'upload'` or `/uploads/...` / `.mp4`/`.webm`/`.mov`)
 * and external video URLs (YouTube Shorts, standard YouTube videos, and external direct video links).
 *
 * @param {object} item - Raw backend record
 * @param {number} index - Index for fallback ID / order
 * @returns {object|null} Normalized video object
 */
export function formatVideoItem(item, index = 0) {
    if (!item || typeof item !== 'object') return null;

    const rawId = item._id || item.id || `video-${index}`;
    const rawSourceType = String(
        item.source_type || item.sourceType || item.video_type || item.videoType || ''
    )
        .trim()
        .toLowerCase();

    const isExplicitUpload =
        rawSourceType === 'upload' ||
        rawSourceType === 'uploaded' ||
        rawSourceType === 'file' ||
        rawSourceType === 'local' ||
        rawSourceType === 'direct';

    const explicitOptimizedUrl = extractUrlString(
        item.optimized_video_url ||
            item.optimizedVideoUrl ||
            item.stream_url ||
            item.streamUrl
    );

    const baseRawUrl = isExplicitUpload
        ? extractUrlString(
              item.uploaded_video_url ||
                  item.uploadedVideoUrl ||
                  item.file_url ||
                  item.fileUrl ||
                  item.video_file ||
                  item.videoFile ||
                  item.video_path ||
                  item.videoPath ||
                  item.video_url ||
                  item.videoUrl ||
                  item.url ||
                  item.source ||
                  item.video_source
          )
        : extractUrlString(
              item.video_url ||
                  item.videoUrl ||
                  item.uploaded_video_url ||
                  item.uploadedVideoUrl ||
                  item.file_url ||
                  item.fileUrl ||
                  item.video_file ||
                  item.videoFile ||
                  item.video_path ||
                  item.videoPath ||
                  item.url ||
                  item.source ||
                  item.video_source
          );

    const rawUrl = baseRawUrl || explicitOptimizedUrl;
    const preferredRawUrl = explicitOptimizedUrl || rawUrl;

    const youtubeId = isExplicitUpload ? null : extractYouTubeId(rawUrl);
    const isEmbed = !isExplicitUpload && !youtubeId && isKnownExternalEmbedUrl(rawUrl);

    const mimeType = String(item.mime_type || item.mimeType || item.file_type || item.fileType || '')
        .trim()
        .toLowerCase();

    const isDirect =
        !youtubeId &&
        !isEmbed &&
        (isExplicitUpload ||
            isDirectVideoUrl(preferredRawUrl) ||
            isDirectVideoUrl(rawUrl) ||
            mimeType.startsWith('video/') ||
            Boolean(item.is_direct_video || item.isDirectVideo || item.video_file || item.videoFile) ||
            Boolean(preferredRawUrl));

    const initialMedia = inferInitialOrientation(item, rawUrl, youtubeId, rawSourceType);
    const isYouTubeShort = Boolean(youtubeId && initialMedia.isYouTubeShort !== false);

    const videoUpdatedAt =
        item.video_updated_at ||
        item.videoUpdatedAt ||
        item.updated_at ||
        item.updatedAt ||
        null;

    // Resolve direct/uploaded video URLs with deterministic cache version & safe streaming optimization
    const resolvedRawVideoSrc =
        isDirect && rawUrl
            ? appendVideoCacheVersion(resolveMediaUrl(rawUrl), videoUpdatedAt)
            : null;
    const resolvedPreferredSrc =
        isDirect && preferredRawUrl
            ? appendVideoCacheVersion(resolveMediaUrl(preferredRawUrl), videoUpdatedAt)
            : resolvedRawVideoSrc;

    const videoSrc = resolvedPreferredSrc
        ? optimizeShortVideoDeliveryUrl(resolvedPreferredSrc, initialMedia.orientation)
        : null;
    const rawVideoSrc = resolvedRawVideoSrc || videoSrc;

    // Resolve custom thumbnail URL against backend base URL when relative;
    // fallback to YouTube thumbnail ONLY for YouTube videos (uploaded videos without custom
    // thumbnail_url use '' so cards lazy-render a single captured preview frame).
    const rawThumbnail = extractUrlString(
        item.thumbnail_url ||
            item.thumbnailUrl ||
            item.thumbnail ||
            item.poster_url ||
            item.posterUrl ||
            item.poster ||
            item.preview_image ||
            item.previewImage
    );

    const resolvedThumb = rawThumbnail ? resolveMediaUrl(rawThumbnail) || '' : '';
    let thumbnail = resolvedThumb ? optimizeShortPosterUrl(resolvedThumb) : '';
    if (!thumbnail && youtubeId) {
        thumbnail = `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`;
    }

    // Consult in-memory dimensions cache if this video URL was already measured
    const cachedDims =
        !initialMedia.isKnown && (videoSrc || rawVideoSrc)
            ? getCachedVideoDimensions(videoSrc) || getCachedVideoDimensions(rawVideoSrc)
            : null;

    const effectiveOrientation = cachedDims
        ? cachedDims.orientation
        : initialMedia.orientation;
    const effectiveAspectClass = cachedDims
        ? cachedDims.aspectClass
        : initialMedia.aspectClass;
    const effectiveAspectRatio = cachedDims ? cachedDims.ratio : initialMedia.ratio;

    const title = String(item.title || item.caption || `Rahber Corporation Short ${index + 1}`).trim();
    const caption = String(item.caption || item.subtitle || '').trim();
    const description = String(item.description || '').trim();
    const badge = String(item.badge || item.tag || (youtubeId ? 'SHORTS' : 'VIDEO')).trim();

    const rawOrder = item.display_order ?? item.displayOrder ?? item.order;
    const displayOrder = Number.isFinite(Number(rawOrder)) ? Number(rawOrder) : index;

    const rawStatus = String(item.status || '').trim().toLowerCase();
    const isActive =
        item.is_active !== false &&
        item.isActive !== false &&
        rawStatus !== 'inactive' &&
        rawStatus !== 'draft' &&
        item.is_published !== false;

    const youtubeEmbedUrl = youtubeId
        ? `https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&playsinline=1&rel=0&modestbranding=1`
        : null;

    // Resolved link for opening in a new tab
    let watchUrl = null;
    if (youtubeId) {
        watchUrl = isYouTubeShort
            ? `https://www.youtube.com/shorts/${youtubeId}`
            : `https://www.youtube.com/watch?v=${youtubeId}`;
    } else if (rawVideoSrc || videoSrc) {
        watchUrl = rawVideoSrc || videoSrc;
    } else if (rawUrl) {
        watchUrl = resolveMediaUrl(rawUrl) || rawUrl;
    }

    const sourceType = youtubeId
        ? 'youtube'
        : isExplicitUpload
          ? 'upload'
          : isDirect
            ? 'direct'
            : isEmbed
              ? 'embed'
              : 'unknown';

    return {
        _id: String(rawId),
        id: youtubeId || String(rawId),
        rawId,
        sourceType,
        youtubeId,
        isYouTubeShort,
        youtubeEmbedUrl,
        isDirectVideo: isDirect,
        isEmbedVideo: isEmbed,
        embedUrl: isEmbed ? rawUrl : null,
        videoSrc,
        rawVideoSrc,
        mimeType: mimeType || null,
        url: rawUrl,
        watchUrl,
        title,
        caption,
        description,
        badge,
        thumbnail,
        displayOrder,
        isActive,
        initialOrientation: effectiveOrientation,
        initialAspectClass: effectiveAspectClass,
        initialAspectRatio: effectiveAspectRatio,
        raw: item
    };
}

